import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { intersection } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceTabs } from '~/components/SolaForceTabs';
import { useAllLegalEntities } from '~/data/legalEntity';
import { useMyUnits } from '~/data/orgunits';
import {
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
} from '~/data/reports';
import { ReportUnitSelector } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/ReportTypeSelector/ReportUnitSelector';
import { useReportTypeLabels } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/ReportTypeSelector/useReportTypeLabels';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/ReportTypeSelector/useStyles';
import { UseReportDatasetOutput } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/useReportDataset';
import { getNumberOrUndefined } from '~/utils/helper';

type PropsType<P> = Pick<
  UseReportDatasetOutput<P>,
  'reportSelection' | 'reportConfig'
> & {
  onTypeChange: (type: AnalyticsReportTypes) => void;
  onUnitChange: (unitId?: number) => void;
  onLegalEntityChange: (legalEntityId?: number) => void;
  onRoleChange: (role?: AnalyticsReportRoleScope) => void;
  onIncludeSubUnits?: (checked: boolean) => void;
};

export const ReportTypeSelector = <P extends object>(props: PropsType<P>) => {
  const {
    reportSelection,
    reportConfig,
    onTypeChange,
    onUnitChange,
    onRoleChange,
    onLegalEntityChange,
    onIncludeSubUnits,
  } = props;
  const { reportType } = reportSelection;

  const sxStyles = useSxStyles();

  const { t } = useTranslation();

  const { myUnits } = useMyUnits();

  const { legalEntities } = useAllLegalEntities();
  const legalEntityOptions = useMemo(() => {
    return legalEntities
      .filter((le) =>
        reportConfig.options.legalEntities.includes(le.fLegalEntityId),
      )
      .map((le) => {
        return { label: le.fLegalEntityName, id: le.fLegalEntityId };
      });
  }, [legalEntities, reportConfig]);

  const { getTypeLabel, getRoleLabel } = useReportTypeLabels();

  const tabOptions = useMemo(() => {
    const tabsOrder = [
      AnalyticsReportTypes.Company,
      AnalyticsReportTypes.LegalEntity,
      AnalyticsReportTypes.Team,
      AnalyticsReportTypes.HR,
      AnalyticsReportTypes.Unit,
    ];

    return intersection(tabsOrder, reportConfig.options.types);
  }, [reportConfig]);

  const roleOptions = useMemo(() => {
    const roleOrder = [
      AnalyticsReportRoleScope.Manager,
      AnalyticsReportRoleScope.AltManager,
    ];

    return intersection(roleOrder, reportConfig.options.roles).map((role) => {
      return {
        label: getRoleLabel(role),
        id: role,
      };
    });
  }, [reportConfig, getRoleLabel]);

  const IncludeSubUnitsSelector = () => {
    return !!onIncludeSubUnits ? (
      <FormControlLabel
        control={<Checkbox />}
        label={t('label_include_subunits')}
        checked={reportType.scope === AnalyticsReportScope.All}
        onChange={(_event, checked) => onIncludeSubUnits(checked)}
      />
    ) : (
      <></>
    );
  };

  const TypeTabContent = () => {
    switch (reportType.type) {
      case AnalyticsReportTypes.Team:
        return (
          <Box sx={sxStyles.tabContent}>
            <Typography variant={'caption'}>
              {t('label_report_scope_myteam_info')}
            </Typography>
            <Autocomplete
              disablePortal
              options={roleOptions}
              value={roleOptions.find(
                (option) => option.id === reportType.role,
              )}
              onChange={(_event, value) =>
                onRoleChange(!!value ? value.id : undefined)
              }
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField {...params} label={t('laRole')} />
              )}
              fullWidth={true}
              size={'small'}
              disableClearable={true}
              disabled={roleOptions.length < 2}
            />
            <IncludeSubUnitsSelector />
          </Box>
        );
      case AnalyticsReportTypes.HR:
        return (
          <Box sx={sxStyles.tabContent}>
            <Typography variant={'caption'}>
              {t('label_report_scope_hr_info')}
            </Typography>
          </Box>
        );
      case AnalyticsReportTypes.Unit:
        return (
          <Box sx={sxStyles.tabContent}>
            <Typography variant={'caption'}>
              {t('label_report_scope_unit_info')}
            </Typography>
            <ReportUnitSelector
              organizationTree={myUnits}
              value={reportType.unitId}
              onChange={onUnitChange}
              unitOptions={reportConfig.options.units}
            />
            <IncludeSubUnitsSelector />
          </Box>
        );
      case AnalyticsReportTypes.LegalEntity:
        return (
          <Box sx={sxStyles.tabContent}>
            <Typography variant={'caption'}>
              {t('label_report_scope_legal_entity_info')}
            </Typography>
            <Autocomplete
              disablePortal
              options={legalEntityOptions}
              value={legalEntityOptions.find(
                (option) => option.id === reportType.legalEntityId,
              )}
              onChange={(_event, value) =>
                onLegalEntityChange(
                  getNumberOrUndefined(!!value ? value.id : undefined),
                )
              }
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField {...params} label={t('laLegalEntity')} />
              )}
              fullWidth={true}
              size={'small'}
              disableClearable={true}
            />
          </Box>
        );
      case AnalyticsReportTypes.Company:
        return (
          <Typography variant={'caption'}>
            {t('label_report_scope_company_info')}
          </Typography>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <SolaForceTabs
        orientation={'vertical'}
        value={reportType.type}
        onChange={(_event, value) => onTypeChange(value)}
        shadow={'default'}
      >
        {tabOptions.map((tab) => {
          const tabProps = {
            id: `type-tab-${tab}`,
            'aria-controls': `type-tabpanel-${tab}`,
            label: getTypeLabel(tab),
            value: tab,
            key: `type-tab-${tab}`,
          };

          return <Tab {...tabProps}></Tab>;
        })}
      </SolaForceTabs>

      <Box sx={sxStyles.tabFieldSet}>
        <TypeTabContent />
      </Box>
    </>
  );
};
